import { cva, type VariantProps } from "class-variance-authority";
import { CircleIcon } from "lucide-react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import type { ComponentPropsWithRef } from "react";
import { cn } from "@/lib/utils";

const RadioGroup = ({
  className,
  ...props
}: ComponentPropsWithRef<typeof RadioGroupPrimitive.Root>) => (
  <RadioGroupPrimitive.Root
    className={cn("grid gap-3", className)}
    data-slot="radio-group"
    {...props}
  />
);

const RadioGroupItem = ({
  className,
  ...props
}: ComponentPropsWithRef<typeof RadioGroupPrimitive.Item>) => (
  <RadioGroupPrimitive.Item
    className={cn(
      "aspect-square size-4 shrink-0 rounded-full border border-input text-primary shadow-xs outline-none transition-[color,box-shadow] focus-visible:ring-4 focus-visible:ring-ring/10 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 aria-invalid:focus-visible:ring-0 dark:bg-input/30 dark:aria-invalid:ring-destructive/40 dark:focus-visible:ring-ring/60",
      className
    )}
    data-slot="radio-group-item"
    {...props}
  >
    <RadioGroupPrimitive.Indicator
      className="relative flex items-center justify-center"
      data-slot="radio-group-indicator"
    >
      <CircleIcon className="absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 fill-primary" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
);

const radioGroupCardItemVariants = cva(
  "rounded-lg bg-card text-card-foreground text-sm shadow-xs outline-none ring-offset-background focus-visible:ring-4 focus-visible:ring-ring/10 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-ring/60",
  {
    variants: {
      variant: {
        default:
          "border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
        destructive:
          "border data-[state=checked]:bg-destructive data-[state=checked]:text-destructive-foreground",
        outline: "border-2 data-[state=checked]:border-primary",
        secondary:
          "border data-[state=checked]:bg-secondary data-[state=checked]:text-secondary-foreground",
        ghost:
          "data-[state=checked]:bg-accent data-[state=checked]:text-accent-foreground",
      },
      size: {
        default: "p-4 has-[>svg]:px-3",
        sm: "px-2 py-4 has-[>svg]:px-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const RadioGroupCardItem = ({
  variant,
  size,
  className,
  ...props
}: ComponentPropsWithRef<typeof RadioGroupPrimitive.Item> &
  VariantProps<typeof radioGroupCardItemVariants>) => (
  <RadioGroupPrimitive.Item
    className={cn(radioGroupCardItemVariants({ variant, size, className }))}
    data-slot="radio-group-card-item"
    {...props}
  />
);

export { RadioGroup, RadioGroupItem, RadioGroupCardItem };
